import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { colors, fontFamily, maxWidth } from "./tokens";
import Reveal from "./Reveal";

const useStyles = makeStyles(theme => ({
  section: {
    padding: theme.spacing(12, 3, 14),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(8, 2.5, 8)
    }
  },
  inner: {
    maxWidth,
    margin: "0 auto"
  },
  head: {
    textAlign: "center",
    maxWidth: 560,
    margin: "0 auto",
    marginBottom: theme.spacing(8)
  },
  headline: {
    fontFamily,
    fontWeight: 700,
    letterSpacing: "-0.03em",
    fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
    color: colors.ink,
    margin: 0,
    marginBottom: theme.spacing(2)
  },
  sub: {
    fontFamily,
    fontSize: 15,
    lineHeight: 1.65,
    color: colors.inkMuted,
    margin: 0
  },
  composition: {
    display: "grid",
    gridTemplateColumns: "1.3fr 1fr",
    gap: theme.spacing(3),
    alignItems: "stretch",
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr"
    }
  },
  panel: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 14,
    padding: theme.spacing(2.5),
    boxShadow: "0 8px 24px -20px rgba(18,21,26,0.18)",
    display: "flex",
    flexDirection: "column"
  },
  panelLabel: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: colors.inkFaint,
    marginBottom: theme.spacing(1.5)
  },
  assetName: {
    fontFamily,
    fontWeight: 600,
    fontSize: 16,
    color: colors.ink
  },
  assetId: {
    fontSize: 12,
    color: colors.inkMuted,
    marginTop: 2,
    marginBottom: theme.spacing(2)
  },
  metaRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 12.5,
    padding: theme.spacing(0.9, 0),
    borderTop: `1px solid ${colors.border}`,
    color: colors.inkMuted,
    "&:first-of-type": {
      borderTop: "none"
    }
  },
  metaValue: {
    fontWeight: 600,
    color: colors.ink
  },
  progressTrack: {
    height: 6,
    borderRadius: 999,
    background: colors.surfaceMuted,
    overflow: "hidden",
    marginTop: theme.spacing(1)
  },
  progressFill: {
    height: "100%",
    borderRadius: 999,
    background: colors.primary
  },
  stepList: {
    marginTop: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1)
  },
  stepRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12.5,
    color: colors.inkMuted
  },
  stepDone: {
    color: colors.ink
  },
  check: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    background: colors.primarySoft,
    color: colors.primaryDark,
    fontSize: 9,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  checkPending: {
    background: colors.surfaceMuted,
    color: colors.inkFaint
  },
  activityRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    fontSize: 12.5,
    padding: theme.spacing(0.9, 0),
    borderTop: `1px solid ${colors.border}`,
    "&:first-of-type": {
      borderTop: "none"
    }
  },
  activityName: {
    color: colors.ink,
    fontWeight: 500
  },
  activityTime: {
    color: colors.inkFaint,
    fontSize: 11
  },
  stockBar: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 12.5,
    marginBottom: 4,
    color: colors.inkMuted
  }
}));

export default function ProductShowcase() {
  const classes = useStyles();

  return (
    <section className={classes.section}>
      <div className={classes.inner}>
        <div className={classes.head}>
          <Reveal as="h2" className={classes.headline}>
            Built around how maintenance actually works.
          </Reveal>
          <Reveal as="p" className={classes.sub} delay={80}>
            Connect the people, assets, work and materials involved in every
            maintenance operation.
          </Reveal>
        </div>

        <div className={classes.composition}>
          <Reveal as="div" className={classes.panel}>
            <div className={classes.panelLabel}>Asset details</div>
            <div className={classes.assetName}>Rooftop Chiller Unit 2</div>
            <div className={classes.assetId}>AST-10432 &middot; Building A</div>
            <div className={classes.metaRow}>
              <span>Condition</span>
              <span className={classes.metaValue}>Good</span>
            </div>
            <div className={classes.metaRow}>
              <span>Responsible technician</span>
              <span className={classes.metaValue}>M. Johnson</span>
            </div>
            <div className={classes.metaRow}>
              <span>Last serviced</span>
              <span className={classes.metaValue}>14 days ago</span>
            </div>
          </Reveal>

          <Reveal as="div" className={classes.panel} delay={40}>
            <div className={classes.panelLabel}>Technician activity</div>
            <div className={classes.activityRow}>
              <span className={classes.activityName}>M. Johnson</span>
              <span className={classes.activityTime}>Onsite</span>
            </div>
            <div className={classes.activityRow}>
              <span className={classes.activityName}>T. Adisa</span>
              <span className={classes.activityTime}>32m ago</span>
            </div>
            <div className={classes.activityRow}>
              <span className={classes.activityName}>K. Okoro</span>
              <span className={classes.activityTime}>1h ago</span>
            </div>
          </Reveal>

          <Reveal as="div" className={classes.panel} delay={80}>
            <div className={classes.panelLabel}>Work order progress</div>
            <div className={classes.assetName}>WO-2291</div>
            <div className={classes.progressTrack}>
              <div className={classes.progressFill} style={{ width: "70%" }} />
            </div>
            <div className={classes.stepList}>
              <div className={`${classes.stepRow} ${classes.stepDone}`}>
                <span className={classes.check}>&#10003;</span>
                Accepted
              </div>
              <div className={`${classes.stepRow} ${classes.stepDone}`}>
                <span className={classes.check}>&#10003;</span>
                Observation reported
              </div>
              <div className={classes.stepRow}>
                <span className={`${classes.check} ${classes.checkPending}`}>3</span>
                Awaiting completion
              </div>
            </div>
          </Reveal>

          <Reveal as="div" className={classes.panel} delay={120}>
            <div className={classes.panelLabel}>Inventory status</div>
            <div className={classes.stockBar}>
              <span>Compressor belts</span>
              <span>12 in stock</span>
            </div>
            <div className={classes.progressTrack}>
              <div className={classes.progressFill} style={{ width: "80%" }} />
            </div>
            <div className={classes.stockBar} style={{ marginTop: 12 }}>
              <span>Refrigerant (R410A)</span>
              <span>Low stock</span>
            </div>
            <div className={classes.progressTrack}>
              <div className={classes.progressFill} style={{ width: "22%" }} />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
